import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const GestionZoomClases = () => {
  const { API } = useContext(AuthContext);
  const [meetings, setMeetings] = useState([]);
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [asociando, setAsociando] = useState({});

  useEffect(() => {
    loadData();
  }, [API]);

  const buscarMejorCoincidencia = (clase, meetings) => {
    // Buscar reunión que coincida con el nombre de la clase y/o profesor
    const nombreClase = (clase.nombre || clase.titulo || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const nombreProfesor = (clase.profesor_nombre || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    let mejorCoincidencia = null;
    let mejorScore = 0;
    
    meetings.forEach(meeting => {
      const topicMeeting = meeting.topic.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      let score = 0;
      
      // Buscar palabras clave del nombre de la clase en el topic
      const palabrasClase = nombreClase.split(' ');
      palabrasClase.forEach(palabra => {
        if (palabra.length > 3 && topicMeeting.includes(palabra)) {
          score += 2;
        }
      });
      
      // Buscar palabras del profesor
      const palabrasProfesor = nombreProfesor.split(' ');
      palabrasProfesor.forEach(palabra => {
        if (palabra.length > 2 && topicMeeting.includes(palabra)) {
          score += 3;
        }
      });
      
      if (score > mejorScore) {
        mejorScore = score;
        mejorCoincidencia = meeting;
      }
    });
    
    // Retornar solo si hay una coincidencia razonable
    return mejorScore >= 3 ? mejorCoincidencia : null;
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar clases activas PRIMERO (rápido)
      console.log('🔄 Cargando clases activas...');
      const clasesResponse = await axios.get(`${API}/clases`);
      const clasesActivas = clasesResponse.data.filter(c => c.activa);
      console.log('✅ Clases activas cargadas:', clasesActivas.length);
      setClases(clasesActivas);
      
      // Mostrar la página inmediatamente
      setLoading(false);

      // Cargar reuniones de Zoom en segundo plano (más lento)
      console.log('🔄 Cargando reuniones de Zoom en segundo plano...');
      toast.info('Cargando reuniones de Zoom...', { autoClose: 2000 });
      
      const meetingsResponse = await axios.get(`${API}/admin/zoom/meetings`);
      const zoomMeetings = meetingsResponse.data.meetings || [];
      console.log('✅ Reuniones de Zoom cargadas:', zoomMeetings.length);
      setMeetings(zoomMeetings);
      
      toast.success(`${zoomMeetings.length} reuniones de Zoom disponibles`, { autoClose: 2000 });

    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      toast.error('Error cargando datos');
      setLoading(false);
    }
  };

  const sincronizarMeetings = async () => {
    try {
      setSyncing(true);
      await loadData();
      const count = meetings.length;
      toast.success(`✅ ${count} reuniones de Zoom sincronizadas`);
    } catch (error) {
      toast.error('Error sincronizando reuniones');
      console.error('Error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const asociarTodoAutomaticamente = async () => {
    try {
      setAsociando({ ...asociando, auto: true });
      
      let asociadas = 0;
      let sinCoincidencia = 0;
      
      for (const clase of clases) {
        // Solo procesar clases sin zoom asociado
        if (clase.zoom_start_url) continue;
        
        // Buscar mejor coincidencia
        const mejorMeeting = buscarMejorCoincidencia(clase, meetings);
        
        if (mejorMeeting) {
          console.log(`✅ Asociando: ${clase.titulo || clase.nombre} ↔ ${mejorMeeting.topic}`);
          
          await axios.post(`${API}/admin/clases/${clase.id}/asociar-zoom`, {
            meeting_id: mejorMeeting.id,
            join_url: mejorMeeting.join_url,
            start_url: mejorMeeting.start_url,
            topic: mejorMeeting.topic
          });
          
          asociadas++;
        } else {
          sinCoincidencia++;
          console.log(`⚠️ Sin coincidencia para: ${clase.titulo || clase.nombre}`);
        }
      }
      
      // Recargar datos
      await loadData();
      
      if (asociadas > 0) {
        toast.success(`✅ ${asociadas} clases asociadas automáticamente`);
      }
      
      if (sinCoincidencia > 0) {
        toast.warning(`⚠️ ${sinCoincidencia} clases sin coincidencia (debes asociar manualmente)`);
      }
      
      if (asociadas === 0 && sinCoincidencia === 0) {
        toast.info('ℹ️ Todas las clases ya tienen Zoom asociado');
      }
      
    } catch (error) {
      console.error('Error en asociación automática:', error);
      toast.error('Error en asociación automática');
    } finally {
      setAsociando({ ...asociando, auto: false });
    }
  };

  const asociarMeeting = async (claseId, meeting) => {
    try {
      setAsociando({ ...asociando, [claseId]: true });

      console.log('🔄 Asociando reunión:', {
        claseId,
        meetingId: meeting.id,
        meetingTopic: meeting.topic
      });

      const response = await axios.post(
        `${API}/admin/clases/${claseId}/asociar-zoom`, 
        {
          meeting_id: meeting.id,
          join_url: meeting.join_url,
          start_url: meeting.start_url,
          topic: meeting.topic
        }
      );

      console.log('✅ Respuesta del servidor:', response.data);
      toast.success('Reunión de Zoom asociada exitosamente');
      
      // Recargar clases
      await loadData();

    } catch (error) {
      console.error('❌ Error asociando reunión:', error);
      console.error('   Error response:', error.response?.data);
      console.error('   Error status:', error.response?.status);
      
      const errorMsg = error.response?.data?.detail || 'Error al asociar reunión de Zoom';
      toast.error(errorMsg);
    } finally {
      setAsociando({ ...asociando, [claseId]: false });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Cargando Gestión de Zoom</h3>
          <p className="text-gray-500">Obteniendo clases y reuniones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">
          <span className="mr-2">🎥</span>
          Gestión de Zoom
        </h1>
        <div className="flex gap-3">
          <Button
            onClick={asociarTodoAutomaticamente}
            disabled={syncing || asociando.auto || meetings.length === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {asociando.auto ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Asociando...
              </>
            ) : (
              <>
                <span className="mr-2">⚡</span>
                Asociar Automáticamente
              </>
            )}
          </Button>
          <Button
            onClick={sincronizarMeetings}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {syncing ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Sincronizando...
              </>
            ) : (
              <>
                <span className="mr-2">🔄</span>
                Sincronizar Reuniones
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">{meetings.length}</div>
              <div className="text-sm text-gray-600 mt-1">Reuniones en Zoom</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">
                {clases.filter(c => c.zoom_start_url).length}
              </div>
              <div className="text-sm text-gray-600 mt-1">Clases con Zoom</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600">
                {clases.filter(c => !c.zoom_start_url).length}
              </div>
              <div className="text-sm text-gray-600 mt-1">Clases sin Zoom</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de clases */}
      <Card>
        <CardHeader>
          <CardTitle>Asociar Reuniones de Zoom con Clases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {clases.map(clase => (
              <div
                key={clase.id}
                className="border rounded-lg p-4 hover:border-blue-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Información de la clase */}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-800">
                      {clase.titulo || clase.nombre}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Profesor: {clase.profesor_nombre || 'Sin asignar'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Horario: {clase.dias_semana?.join(', ') || clase.dia_semana || 'No definido'}
                    </p>
                    
                    {/* Estado de asociación */}
                    {clase.zoom_start_url ? (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✅ Reunión Asociada
                        </span>
                        <span className="text-xs text-gray-500">
                          {clase.zoom_topic}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          ⚠️ Sin Reunión
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Selector de reunión */}
                  <div className="w-80">
                    {meetings.length === 0 ? (
                      <div className="text-sm text-yellow-600 bg-yellow-50 px-3 py-2 rounded-md border border-yellow-300">
                        ⚠️ No hay reuniones de Zoom. Click en "Sincronizar Reuniones"
                      </div>
                    ) : (() => {
                      const sugerencia = buscarMejorCoincidencia(clase, meetings);
                      
                      return (
                        <div className="space-y-2">
                          {sugerencia && !clase.zoom_start_url && (
                            <div className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-300">
                              💡 Sugerencia: {sugerencia.topic}
                            </div>
                          )}
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onChange={(e) => {
                              if (e.target.value) {
                                const meeting = meetings.find(m => String(m.id) === e.target.value);
                                if (meeting) {
                                  console.log('Asociando reunión:', meeting.topic, 'con clase:', clase.nombre);
                                  asociarMeeting(clase.id, meeting);
                                }
                              }
                            }}
                            disabled={asociando[clase.id]}
                            value=""
                          >
                            <option value="">
                              {asociando[clase.id] ? 'Asociando...' : `Seleccionar reunión (${meetings.length} disponibles)`}
                            </option>
                            {meetings.map(meeting => {
                              const esSugerencia = sugerencia && meeting.id === sugerencia.id;
                              return (
                                <option key={meeting.id} value={String(meeting.id)}>
                                  {esSugerencia ? '⭐ ' : ''}
                                  {meeting.is_active ? '✅ ' : '❌ '}
                                  {meeting.topic}
                                  {meeting.next_occurrence && ` (Próx: ${new Date(meeting.next_occurrence).toLocaleDateString('es-UY')})`}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}

            {clases.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay clases activas para asociar
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reuniones de Zoom disponibles */}
      <Card>
        <CardHeader>
          <CardTitle>Reuniones de Zoom Disponibles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {meetings.map(meeting => (
              <div
                key={meeting.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {meeting.is_active ? '✅' : '⏸️'}
                    </span>
                    <span className="font-medium">{meeting.topic}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Tipo: {meeting.type === 'recurrente' ? 'Recurrente' : 'Única'}
                    {meeting.next_occurrence && (
                      <> • Próxima: {new Date(meeting.next_occurrence).toLocaleString('es-UY')}</>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  ID: {meeting.id}
                </div>
              </div>
            ))}

            {meetings.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay reuniones de Zoom disponibles
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionZoomClases;
